/**
 * Discover Groups Component
 * Displays public groups for discovery and joining
 */

import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Avatar,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material'
import {
  Groups as GroupsIcon,
  PersonAdd as JoinIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { GroupSearchRequest } from '@api/types/social'

const GROUP_ICON_COLORS = [
  { bg: 'rgba(66,153,225,0.12)', color: '#4299e1' },
  { bg: 'rgba(56,178,172,0.12)', color: '#38b2ac' },
  { bg: 'rgba(212,168,83,0.12)', color: '#d4a853' },
  { bg: 'rgba(159,122,234,0.12)', color: '#9f7aea' },
  { bg: 'rgba(72,187,120,0.12)', color: '#48bb78' },
]

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(135deg, #f093fb, #f5576c)',
  'linear-gradient(135deg, #4facfe, #00f2fe)',
  'linear-gradient(135deg, #43e97b, #38f9d7)',
  'linear-gradient(135deg, #fa709a, #fee140)',
  'linear-gradient(135deg, #a18cd1, #fbc2eb)',
  'linear-gradient(135deg, #fccb90, #d57eeb)',
]

function getGroupIconColor(name: string) {
  let hash = 0
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return GROUP_ICON_COLORS[Math.abs(hash) % GROUP_ICON_COLORS.length]
}

interface DiscoverGroupsProps {
  filterText?: string
}

export function DiscoverGroups({ filterText = '' }: DiscoverGroupsProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const searchRequest: GroupSearchRequest = {
    query: filterText || undefined,
    excludeJoined: true,
    take: 50,
    sortBy: 'memberCount',
    sortDescending: true,
  }

  const { data: searchResponse, isLoading, error } = useQuery({
    queryKey: ['discover-groups', filterText],
    queryFn: () => socialApi.searchGroups(searchRequest),
  })

  const groups = searchResponse?.groups || []

  const joinGroupMutation = useMutation({
    mutationFn: (groupId: string) => socialApi.joinGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discover-groups'] })
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
    },
  })

  const handleJoinGroup = (e: React.MouseEvent, groupId: string, groupName: string) => {
    e.stopPropagation()
    if (confirm(`Join ${groupName}?`)) {
      joinGroupMutation.mutate(groupId)
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Failed to load groups'}
      </Alert>
    )
  }

  if (!groups || groups.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, opacity: 0.5 }}>
        <GroupsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No Groups Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {filterText ? 'Try adjusting your search' : 'No public groups available yet'}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {groups.map((group) => {
        const iconColor = getGroupIconColor(group.name)
        const memberCount = group.memberCount || 0
        const stackCount = Math.min(memberCount, 3)
        const remaining = memberCount - stackCount

        return (
          <Box
            key={group.id}
            onClick={() => navigate(`/groups/${group.id}`)}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '14px',
              p: '14px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              transition: 'background 0.15s',
              '&:hover': {
                filter: 'brightness(1.1)',
              },
            }}
          >
            {/* Top: Icon + Name + Description + Join */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
              <Avatar
                src={group.groupImage}
                variant="rounded"
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  bgcolor: iconColor.bg,
                  color: iconColor.color,
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                <GroupsIcon />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
                  {group.name}
                </Typography>
                {group.description && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: 'text.secondary',
                      mt: 0.25,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {group.description}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                {!group.isUserMember ? (
                  <Tooltip title="Join Group">
                    <Button
                      size="small"
                      onClick={(e) => handleJoinGroup(e, group.id, group.name)}
                      disabled={joinGroupMutation.isPending}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '10px',
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: 'rgba(72,187,120,0.12)',
                        border: '1px solid rgba(72,187,120,0.25)',
                        color: 'success.main',
                        '&:hover': { bgcolor: 'rgba(72,187,120,0.2)' },
                      }}
                    >
                      Join
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip title="View Group">
                    <Button
                      size="small"
                      onClick={() => navigate(`/groups/${group.id}`)}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '10px',
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: 'rgba(66,153,225,0.1)',
                        color: 'info.main',
                        '&:hover': { bgcolor: 'rgba(66,153,225,0.2)' },
                      }}
                    >
                      View
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Bottom: Member stack + count */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                pt: 1.25,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', mr: 1 }}>
                {Array.from({ length: stackCount }).map((_, i) => {
                  const initial = String.fromCharCode(65 + ((group.name.charCodeAt(i) || 65) % 26))
                  return (
                    <Avatar
                      key={i}
                      sx={{
                        width: 22,
                        height: 22,
                        fontSize: 9,
                        fontWeight: 700,
                        ml: i === 0 ? 0 : '-8px',
                        border: '2px solid',
                        borderColor: 'background.paper',
                        background: AVATAR_GRADIENTS[(i + group.name.charCodeAt(0)) % AVATAR_GRADIENTS.length],
                      }}
                    >
                      {initial}
                    </Avatar>
                  )
                })}
                {remaining > 0 && (
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: 9,
                      fontWeight: 600,
                      ml: '-8px',
                      border: '2px solid',
                      borderColor: 'background.paper',
                      bgcolor: 'rgba(255,255,255,0.08)',
                      color: 'text.secondary',
                    }}
                  >
                    +{remaining}
                  </Avatar>
                )}
              </Box>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{memberCount}</Box> member{memberCount !== 1 ? 's' : ''}
              </Typography>
              <Box
                sx={{
                  ml: 'auto',
                  px: 1,
                  py: 0.25,
                  borderRadius: '6px',
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: 'rgba(255,255,255,0.05)',
                  color: 'text.secondary',
                }}
              >
                Public
              </Box>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
